import React from 'react';
import type { WidgetManifest } from '../../services/myToolsApi';
import { describeWidgetPermissions, type PermissionDescription } from './widgetPermissions';
import '../../styles/Widgets.css';

export interface WidgetPermissionListProps {
  manifest: WidgetManifest | null | undefined;
  /** `list` = full rows with descriptions (install prompt, review queue); `chips` = compact inline badges. */
  variant?: 'list' | 'chips';
  emptyText?: string;
  className?: string;
  /** List variant only: show a checkbox per permission so the installer can grant a subset. */
  selectable?: boolean;
  selected?: Set<string>;
  onToggle?: (key: string, on: boolean) => void;
}

const RISK_LABEL: Record<PermissionDescription['risk'], string> = { high: 'High', medium: 'Medium', low: 'Low' };

/** Renders a manifest's permissions in plain language so people know what they are letting run. */
const WidgetPermissionList: React.FC<WidgetPermissionListProps> = ({ manifest, variant = 'list', emptyText, className, selectable = false, selected, onToggle }) => {
  const items = describeWidgetPermissions(manifest);
  const isOn = (key: string) => (selected ? selected.has(key) : true);

  if (items.length === 0) {
    return (
      <span className={`wg-chip${className ? ` ${className}` : ''}`} title="Runs in an isolated sandbox: no agency data, no network, no access to Momentum AMP itself.">
        🔒 {emptyText ?? 'No extra permissions — isolated, scripts only'}
      </span>
    );
  }

  if (variant === 'chips') {
    return (
      <div className={`wg-perm-chips${className ? ` ${className}` : ''}`}>
        {items.map(p => (
          <span key={p.key} className={`wg-chip wg-chip--perm-${p.group}`} title={`${p.key} — ${p.description}`}>
            {p.icon} {p.label}
          </span>
        ))}
      </div>
    );
  }

  return (
    <ul className={`wg-perm${className ? ` ${className}` : ''}`}>
      {items.map(p => (
        <li key={p.key} className={`wg-perm__item wg-perm__item--${p.risk}${selectable && !isOn(p.key) ? ' wg-perm__item--off' : ''}`}>
          {selectable && (
            <input
              type="checkbox"
              className="wg-perm__toggle"
              checked={isOn(p.key)}
              onChange={e => onToggle?.(p.key, e.target.checked)}
              aria-label={`Allow: ${p.label}`}
            />
          )}
          <span className="wg-perm__icon" aria-hidden="true">{p.icon}</span>
          <div className="wg-perm__text">
            <div className="wg-perm__label">
              {p.label}
              <code className="wg-perm__key">{p.key}</code>
            </div>
            <p className="wg-perm__desc">{p.description}</p>
          </div>
          <span className={`wg-perm__risk wg-perm__risk--${p.risk}`}>{RISK_LABEL[p.risk]}</span>
        </li>
      ))}
    </ul>
  );
};

export default React.memo(WidgetPermissionList);
